import {
  ALL_FORMATS,
  Input,
  UrlSource,
  VideoSampleSink,
  type InputVideoTrack,
  type VideoSample,
} from "mediabunny";
import { createConcurrencyLimiter } from "./limit-concurrency";
import { boundedRangeFetch, STILL_RANGE_BYTES } from "./bounded-range-fetch";

/**
 * Per-source byte cache. One frame needs the header, the moov atom, and the
 * GOP containing it, so a few MiB covers a scrub across the clip. Mediabunny's
 * own default is 64 MiB, which a grid of thumbnails would multiply by the
 * number of distinct clips on screen.
 */
const MAX_CACHE_BYTES = 8 * 1024 * 1024;

/** Encoded frames held before the oldest is revoked. Each is ~100-200 KB. */
const MAX_CACHED_FRAMES = 128;

/**
 * Extractions in flight at once. A grid mounts a card per ad, and letting them
 * all decode at once splits the bandwidth so many ways that they finish
 * together after a long wait. A few at a time fills thumbnails in
 * progressively, which is both faster to first paint and steadier to watch.
 */
const MAX_CONCURRENT_EXTRACTIONS = 4;

/**
 * Requests mediabunny may have in flight against one clip. Its default of 2
 * costs a still roughly double: the two workers start about 64 KiB apart and
 * each open-ended range is capped to the same window, so the pair overlaps by
 * ~94%. A page of lane covers measured 12.6 MB fetched for 7.7 MB of distinct
 * bytes. One worker reads the same GOP once.
 *
 * ponytail: playback wants the parallelism, stills do not. Only stills own
 * their source here; the playing layer's is built inside `@remotion/media`.
 */
const STILL_READ_PARALLELISM = 1;

const withExtractionSlot = createConcurrencyLimiter(MAX_CONCURRENT_EXTRACTIONS);

const FRAME_MIME = "image/webp";
const FRAME_QUALITY = 0.92;

/** A track's sample sink alongside the track, which knows where it starts. */
interface VideoTrackReader {
  sink: VideoSampleSink;
  track: InputVideoTrack;
}

/**
 * One sink per source URL, shared by every caller that reads that clip: the
 * ranged reads and the decoded GOP are paid for once rather than once per
 * frame. This is what makes a track of ten slices of the same UGC source cost
 * one read. Resolves to null when the source has no video track.
 *
 * ponytail: entries are never evicted, so a session that views many distinct
 * clips holds one bounded byte cache per clip. Add an LRU keyed on last use if
 * that becomes real memory.
 */
const sinkBySrc = new Map<string, Promise<VideoTrackReader | null>>();

function getSink(src: string): Promise<VideoTrackReader | null> {
  const existing = sinkBySrc.get(src);
  if (existing !== undefined) {
    return existing;
  }

  const promise = (async () => {
    const input = new Input({
      formats: ALL_FORMATS,
      source: new UrlSource(src, {
        maxCacheSize: MAX_CACHE_BYTES,
        parallelism: STILL_READ_PARALLELISM,
        fetchFn: boundedRangeFetch(fetch, STILL_RANGE_BYTES),
      }),
    });
    const track: InputVideoTrack | null = await input.getPrimaryVideoTrack();
    return track ? { sink: new VideoSampleSink(track), track } : null;
  })();

  // A rejected probe (404, expired signature, undecodable file) must not be
  // cached, or the clip can never recover within the session.
  promise.catch(() => {
    if (sinkBySrc.get(src) === promise) sinkBySrc.delete(src);
  });
  sinkBySrc.set(src, promise);
  return promise;
}

/**
 * Extracted frames as blob URLs. A settled entry is a string, so a caller can
 * read it during render and paint on its first paint instead of decoding again
 * and flashing empty. This is what makes a virtualized grid cheap: cards
 * unmount as they scroll out and remount on the way back, and a hit here is
 * the difference between repainting and re-decoding.
 *
 * Least-recently-used: a Map iterates in insertion order, so reading an entry
 * reinserts it and eviction takes the front. Discarding by age instead would
 * evict exactly the frames a scroll back to the top asks for first.
 */
const frameCache = new Map<string, string | Promise<string>>();

/** Cache key for a frame. Callers hold these to look a request back up. */
export function videoFrameKey(src: string, timestampInSeconds: number): string {
  return `${src}@${timestampInSeconds}`;
}

/** Read an entry and mark it most recently used. */
function touchFrame(key: string): string | Promise<string> | undefined {
  const cached = frameCache.get(key);
  if (cached === undefined) return undefined;
  frameCache.delete(key);
  frameCache.set(key, cached);
  return cached;
}

/** The frame for this source and timestamp when one has already been extracted. */
export function settledVideoFrameUrl(
  src: string,
  timestampInSeconds: number,
): string | undefined {
  const cached = touchFrame(videoFrameKey(src, timestampInSeconds));
  return typeof cached === "string" ? cached : undefined;
}

function rememberFrame(key: string, url: string): void {
  frameCache.set(key, url);
  while (frameCache.size > MAX_CACHED_FRAMES) {
    const oldest = frameCache.keys().next();
    if (oldest.done) break;
    const evicted = frameCache.get(oldest.value);
    frameCache.delete(oldest.value);
    // Revoking an in-flight entry would break its pending consumers, but a
    // promise can only be evicted after MAX_CACHED_FRAMES later extractions.
    if (typeof evicted === "string") URL.revokeObjectURL(evicted);
  }
}

/**
 * The frame at `timestampInSeconds`, or the track's first frame when the
 * request falls before it. A track need not start at zero: an edit list or an
 * encoder offset can put the first packet a frame or two in (0.041s on a 24fps
 * clip), and `getSample` answers null below that, which would leave the lane
 * cover of an untrimmed clip blank.
 */
export async function sampleAtOrAfterStart(
  { sink, track }: VideoTrackReader,
  timestampInSeconds: number,
): Promise<VideoSample | null> {
  const sample = await sink.getSample(timestampInSeconds);
  if (sample) return sample;

  const firstTimestamp = await track.getFirstTimestamp();
  return firstTimestamp > timestampInSeconds
    ? sink.getSample(firstTimestamp)
    : null;
}

async function encodeFrame(
  src: string,
  timestampInSeconds: number,
): Promise<string> {
  const reader = await getSink(src);
  if (!reader) throw new Error(`No video track in ${src}`);

  const sample = await sampleAtOrAfterStart(reader, timestampInSeconds);
  if (!sample) {
    throw new Error(`No frame at ${timestampInSeconds}s in ${src}`);
  }

  try {
    const canvas = document.createElement("canvas");
    canvas.width = sample.displayWidth;
    canvas.height = sample.displayHeight;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("2d canvas context unavailable");
    sample.draw(context, 0, 0);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, FRAME_MIME, FRAME_QUALITY),
    );
    if (!blob) throw new Error("Failed to encode extracted frame");
    return URL.createObjectURL(blob);
  } finally {
    sample.close();
  }
}

/**
 * A blob URL for one decoded frame of `src`, seeking rather than downloading:
 * mediabunny reads the header, the moov atom, and the one GOP the frame sits
 * in. Concurrent and repeat callers for the same frame share one extraction.
 *
 * The blob URL is cached and revoked on eviction, so callers must not revoke
 * it themselves.
 */
export function extractVideoFrameUrl({
  src,
  timestampInSeconds,
}: {
  src: string;
  timestampInSeconds: number;
}): Promise<string> {
  const key = videoFrameKey(src, timestampInSeconds);
  const cached = touchFrame(key);
  if (cached !== undefined) return Promise.resolve(cached);

  // Queued rather than started: the slot covers the size probe and the ranged
  // reads, so a page of cards decodes a few at a time.
  const promise = withExtractionSlot(() =>
    encodeFrame(src, timestampInSeconds),
  );
  frameCache.set(key, promise);
  promise.then(
    (url) => {
      // Only claim the slot if it is still ours; an eviction may have run.
      if (frameCache.get(key) === promise) rememberFrame(key, url);
      else URL.revokeObjectURL(url);
    },
    () => {
      if (frameCache.get(key) === promise) frameCache.delete(key);
    },
  );
  return promise;
}
